import { fetchInsightsSocial } from '../../adminApi';
import { Panel } from '../../ui/Panel';
import { Badge } from '../../ui/Badge';
import { dayMedium } from '../../../core/format/dates';
import { BEST_SLOT_MIN_POSTS } from '../../../core/rules/insights/social';
import { sum } from '../../../core/rules/insights/stats';
import { ChartFrame } from '../charts/ChartFrame';
import { ReportView } from '../ReportView';
import { useReport } from '../useReport';
import { fmt } from '../insightFormat';
import { weekdayLabel } from '../insightLabels';
import type { PeriodState } from '../../sales/usePeriodParams';

/** Instagram: wat elke post deed, de volgers, en het beste moment om te posten. */
export function SocialBlock({
  period
}: {
  period: PeriodState;
}) {
  const report = useReport(fetchInsightsSocial, period.from, period.to);
  return <ReportView report={report} what="De Instagram-cijfers" integration="instagram" data-id="element-1922" data-mp="2871buid8cbbr">
      {s => {
      const first = s.followers[0]?.count ?? null;
      const last = s.followers[s.followers.length - 1]?.count ?? null;
      const reach = s.posts.length ? Math.round(sum(s.posts.map(p => p.reach)) / s.posts.length) : null;
      const tagged = s.posts.filter(p => p.products.length > 0);
      return <>
            <section aria-label="Instagram in deze periode" className="rounded-lg border border-line bg-white p-5 sm:p-6" data-id="element-1923" data-mp="3e4xs3x74ks6j">
              <dl className="grid grid-cols-2 gap-6 lg:grid-cols-4" data-id="element-1924" data-mp="aqbhfbvdm5ti">
                <div className="flex flex-col" data-id="element-1925" data-mp="apiiuy6kmmmg">
                  <dt className="text-[12px] text-muted" data-id="element-1926" data-mp="5qp8hemkzbqi">Volgers</dt>
                  <dd className="mt-1 font-display text-[1.9rem] font-light leading-none tabular-nums text-ink" data-id="element-1927" data-mp="5pe53fx1s5zx">{fmt(last)}</dd>
                  <dd className="mt-1 text-[12px] text-muted" data-id="element-1928" data-mp="3swg65pgfccn9">{first !== null && last !== null ? `${last - first >= 0 ? '+' : '−'}${fmt(Math.abs(last - first))} in deze periode` : '–'}</dd>
                </div>
                <div className="flex flex-col" data-id="element-1929" data-mp="2hghzfs8py2p7">
                  <dt className="text-[12px] text-muted" data-id="element-1930" data-mp="2hiuhs6ie2f2s">Posts</dt>
                  <dd className="mt-1 text-[18px] tabular-nums text-ink" data-id="element-1931" data-mp="2iz88mvqvlg57">{fmt(s.posts.length)}</dd>
                </div>
                <div className="flex flex-col" data-id="element-1932" data-mp="2nw8fh8vjumxd">
                  <dt className="text-[12px] text-muted" data-id="element-1933" data-mp="29xs0kunj9q45">Gemiddeld bereik per post</dt>
                  <dd className="mt-1 text-[18px] tabular-nums text-ink" data-id="element-1934" data-mp="2dyk89jgrbmyb">{fmt(reach)}</dd>
                </div>
                <div className="flex flex-col" data-id="element-1935" data-mp="206xaff7626df">
                  <dt className="text-[12px] text-muted" data-id="element-1936" data-mp="2064el99qx1a2">Beste moment</dt>
                  <dd className="mt-1 text-[18px] text-ink" data-id="element-1937" data-mp="24yxi5ttha3kk">{s.best ? `${weekdayLabel(s.best.weekday)} rond ${String(s.best.hour).padStart(2, '0')}:00` : '–'}</dd>
                  <dd className="mt-1 text-[12px] text-muted" data-id="element-1938" data-mp="1v82tkisrervy">
                    {s.best ? `Op basis van je laatste ${s.postCount} posts` : `Nog te weinig posts (${s.postCount} van ${BEST_SLOT_MIN_POSTS})`}
                  </dd>
                </div>
              </dl>
            </section>

            <ChartFrame title="Per post" description="Bereik = hoeveel accounts de post zagen" tableOnly fileName={`instagram-posts-${period.from}-${period.to}`} table={{
          columns: [{
            label: 'Post'
          }, {
            label: 'Datum',
            kind: 'text'
          }, {
            label: 'Bereik'
          }, {
            label: 'Likes'
          }, {
            label: 'Opgeslagen'
          }, {
            label: 'Gedeeld'
          }, {
            label: 'Profielbezoek'
          }, {
            label: 'Linkkliks'
          }],
          rows: s.posts.map(p => [p.caption, `${dayMedium(p.date)} ${p.time}`, p.reach, p.likes, p.saves, p.shares, p.profileVisits, p.linkClicks])
        }} data-id="element-1939" data-mp="12lumpqdit7eb" />

            <Panel title="Getagde producten" description="Verkocht in de 7 dagen na de post, naast dezelfde 7 dagen een week eerder." data-id="element-1940" data-mp="24aoozht6fevs">
              {tagged.length === 0 ? <p className="text-[13px] text-muted" data-id="element-1941" data-mp="3tsd8hrydnfv3">Geen posts met getagde producten in deze periode. Tag ze in de Social planner.</p> : <ul className="divide-y divide-line text-[13px]" data-id="element-1942" data-mp="2kr765z4rphe">
                  {tagged.map(p => <li key={p.id} className="py-3" data-id="element-1943" data-mp="7dan39laiwpd">
                      <p className="flex flex-wrap items-center gap-2 text-ink" data-id="element-1944" data-mp="bevwgkym8sut">
                        {p.caption}
                        <span className="text-muted" data-id="element-1945" data-mp="3auj3dp0ddohz">{dayMedium(p.date)}</span>
                        {p.plannerPostId && <Badge tone="neutral" data-id="element-1946" data-mp="3ftlwqc6zm6a4">Uit de planner</Badge>}
                      </p>
                      <ul className="mt-1.5 space-y-0.5 text-muted" data-id="element-1947" data-mp="3jtwc2fgo691n">
                        {p.products.map(x => <li key={x.productId} data-id="element-1948" data-mp="3ou21dozgvjgk">
                            {x.name}: <span className="tabular-nums text-ink" data-id="element-1949" data-mp="z46x82fyun8y">{x.unitsAfter}</span> verkocht, de week ervoor {x.unitsBefore}
                          </li>)}
                      </ul>
                    </li>)}
                </ul>}
              <p className="mt-3 text-[12px] leading-relaxed text-muted" data-id="element-1950" data-mp="19859tf8d8pvc">Ter indicatie, geen bewijs: in een drukke week verkoopt alles meer. We volgen niet wie er via Instagram kocht.</p>
            </Panel>
          </>;
    }}
    </ReportView>;
}